import { SYSTEM_UCUM } from '@ctw/shared/api/fhir/system-urls';
import { unitLabel } from '@ctw/shared/api/fhir/unit-labels';
import type { NormalizedUnitSystem } from '@zeus-health/types';
import type { Quantity } from 'fhir/r4';

interface ConversionResult {
  /** Plain numeric total, always in `unit` regardless of which `display` form was picked. */
  value: string;
  unit: string;
  /**
   * Full string to render in place of `${value} ${unit}`, when magnitude picks a different form.
   */
  display?: string;
}

/** Identifies the unit a result is displayed in, so it can be compared with the source unit. */
type ConversionOutcome = ConversionResult & { displayUnit: string };

/**
 * Returns undefined when the source quantity is already shown in its canonical form. Otherwise
 * `displayUnit` names the unit the result displays in, on the same scale as `UNIT_BY_CODE`.
 */
type Conversion = (value: number) => ConversionOutcome | undefined;

const CENTIMETERS_PER_INCH = 2.54;
const CENTIMETERS_PER_FOOT = 30.48;
const INCHES_PER_FOOT = 12;
const GRAMS_PER_KILOGRAM = 1000;
const POUNDS_PER_GRAM = 0.00220462;
const OUNCES_PER_GRAM = 0.035274;
const GRAMS_PER_POUND = 453.59237;
const GRAMS_PER_OUNCE = 28.349523125;
const OUNCES_PER_POUND = 16;

/** Rounds to `decimalPlaces`, breaking ties away from zero rather than toward +Infinity. */
function roundHalfAwayFromZero(value: number, decimalPlaces: number): number {
  const factor = 10 ** decimalPlaces;
  const scaled = value * factor;
  const rounded = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return rounded / factor;
}

function convertCelsiusToFahrenheit(value: number): ConversionOutcome {
  const fahrenheit = roundHalfAwayFromZero((value * 9) / 5 + 32, 1);
  return { value: fahrenheit.toFixed(1), unit: unitLabel('degF'), displayUnit: 'degF' };
}

function convertFahrenheitToCelsius(value: number): ConversionOutcome {
  const celsius = roundHalfAwayFromZero(((value - 32) * 5) / 9, 1);
  return { value: celsius.toFixed(1), unit: unitLabel('Cel'), displayUnit: 'Cel' };
}

/**
 * Under a foot once rounded, a length renders in plain inches; at a foot or more, in feet and a
 * one-decimal inches remainder.
 */
function formatImperialLength(inches: number): ConversionOutcome {
  const totalInches = roundHalfAwayFromZero(inches, 1);
  if (totalInches < INCHES_PER_FOOT) {
    return { value: totalInches.toFixed(1), unit: 'in', displayUnit: 'in' };
  }

  // Whole tenths of an inch keep the feet/inches split exact, so a remainder never rounds past 12.
  const totalTenthInches = Math.round(totalInches * 10);
  const feet = Math.trunc(totalTenthInches / (INCHES_PER_FOOT * 10));
  const remainderInches = (totalTenthInches - feet * INCHES_PER_FOOT * 10) / 10;
  return {
    value: totalInches.toFixed(1),
    unit: 'in',
    display: `${feet}' ${remainderInches.toFixed(1)}"`,
    displayUnit: 'ft-in',
  };
}

function convertCentimetersToImperialLength(centimeters: number): ConversionOutcome {
  return formatImperialLength(centimeters / CENTIMETERS_PER_INCH);
}

/** Leaves a length under a foot, which already renders as plain inches. */
function normalizeInches(inches: number): ConversionOutcome | undefined {
  return roundHalfAwayFromZero(inches, 1) < INCHES_PER_FOOT
    ? undefined
    : formatImperialLength(inches);
}

/**
 * At a pound or more, a weight renders in decimal pounds. Under a pound it renders in ounces,
 * taken from the exact `ounces` rather than the rounded pound value. `isPoundOrMore` is decided by
 * the caller from the exact source figure, not from the rounded pounds.
 */
function formatImperialWeight(
  pounds: number,
  ounces: number,
  isPoundOrMore: boolean,
): ConversionOutcome {
  const totalPounds = roundHalfAwayFromZero(pounds, 1).toFixed(1);
  if (isPoundOrMore) {
    return { value: totalPounds, unit: 'lb', displayUnit: 'lb' };
  }
  return {
    value: totalPounds,
    unit: 'lb',
    display: `${roundHalfAwayFromZero(ounces, 1).toFixed(1)} oz`,
    displayUnit: 'oz',
  };
}

/** `kg` and `g` share this conversion; the threshold compares `grams` against the exact figure. */
function convertGramsToImperialWeight(grams: number): ConversionOutcome {
  return formatImperialWeight(
    grams * POUNDS_PER_GRAM,
    grams * OUNCES_PER_GRAM,
    grams >= GRAMS_PER_POUND,
  );
}

/** Leaves a weight of a pound or more, which already renders as decimal pounds. */
function normalizePounds(pounds: number): ConversionOutcome | undefined {
  return pounds >= 1 ? undefined : formatImperialWeight(pounds, pounds * OUNCES_PER_POUND, false);
}

function convertOuncesToImperialWeight(ounces: number): ConversionOutcome {
  return formatImperialWeight(ounces / OUNCES_PER_POUND, ounces, ounces >= OUNCES_PER_POUND);
}

/**
 * At a kilogram or more, a weight renders in one-decimal kilograms. Under a kilogram it renders in
 * whole grams, with the numeric value kept in kilograms to three decimals.
 */
function formatMetricWeight(grams: number): ConversionOutcome {
  if (grams >= GRAMS_PER_KILOGRAM) {
    return {
      value: roundHalfAwayFromZero(grams / GRAMS_PER_KILOGRAM, 1).toFixed(1),
      unit: 'kg',
      displayUnit: 'kg',
    };
  }
  return {
    value: roundHalfAwayFromZero(grams / GRAMS_PER_KILOGRAM, 3).toFixed(3),
    unit: 'kg',
    display: `${roundHalfAwayFromZero(grams, 0)} g`,
    displayUnit: 'g',
  };
}

/** Leaves a weight of a kilogram or more, which already renders as kilograms. */
function normalizeKilograms(kilograms: number): ConversionOutcome | undefined {
  return kilograms >= 1 ? undefined : formatMetricWeight(kilograms * GRAMS_PER_KILOGRAM);
}

function convertToMetricLength(centimeters: number): ConversionOutcome {
  return { value: roundHalfAwayFromZero(centimeters, 1).toFixed(1), unit: 'cm', displayUnit: 'cm' };
}

const POUND_CODES = ['[lb_av]', 'lb', 'lbs'];
const OUNCE_CODES = ['[oz_av]', 'oz'];
const INCH_CODES = ['[in_i]', '[in_us]', 'in'];
const FOOT_CODES = ['[ft_i]', 'ft'];
const FAHRENHEIT_CODES = ['[degF]', 'degF', '°F'];
const CELSIUS_CODES = ['Cel', '°C'];

function entriesFor(codes: Array<string>, conversion: Conversion): Array<[string, Conversion]> {
  return codes.map((code) => [code, conversion]);
}

/** Imperial conversions, keyed by the UCUM code of the source unit. */
const IMPERIAL_CONVERSIONS_BY_UCUM_CODE = new Map<string, Conversion>([
  ...entriesFor(CELSIUS_CODES, convertCelsiusToFahrenheit),
  ['kg', (value) => convertGramsToImperialWeight(value * GRAMS_PER_KILOGRAM)],
  ['g', convertGramsToImperialWeight],
  ['cm', convertCentimetersToImperialLength],
  ...entriesFor(POUND_CODES, normalizePounds),
  ...entriesFor(OUNCE_CODES, convertOuncesToImperialWeight),
  ...entriesFor(INCH_CODES, normalizeInches),
  ...entriesFor(FOOT_CODES, (value) => formatImperialLength(value * INCHES_PER_FOOT)),
]);

/** Metric conversions, keyed by the UCUM code of the source unit. */
const METRIC_CONVERSIONS_BY_UCUM_CODE = new Map<string, Conversion>([
  ['kg', normalizeKilograms],
  ['g', formatMetricWeight],
  ...entriesFor(POUND_CODES, (value) => formatMetricWeight(value * GRAMS_PER_POUND)),
  ...entriesFor(OUNCE_CODES, (value) => formatMetricWeight(value * GRAMS_PER_OUNCE)),
  ...entriesFor(INCH_CODES, (value) => convertToMetricLength(value * CENTIMETERS_PER_INCH)),
  ...entriesFor(FOOT_CODES, (value) => convertToMetricLength(value * CENTIMETERS_PER_FOOT)),
  ...entriesFor(FAHRENHEIT_CODES, convertFahrenheitToCelsius),
]);

const CONVERSIONS_BY_UNIT_SYSTEM: Record<NormalizedUnitSystem, Map<string, Conversion>> = {
  imperial: IMPERIAL_CONVERSIONS_BY_UCUM_CODE,
  metric: METRIC_CONVERSIONS_BY_UCUM_CODE,
};

/** The unit each source code names, collapsing aliases such as `lb` and `[lb_av]`. */
const UNIT_BY_CODE = new Map<string, string>([
  ...POUND_CODES.map((code): [string, string] => [code, 'lb']),
  ...OUNCE_CODES.map((code): [string, string] => [code, 'oz']),
  ...INCH_CODES.map((code): [string, string] => [code, 'in']),
  ...FOOT_CODES.map((code): [string, string] => [code, 'ft']),
  ...FAHRENHEIT_CODES.map((code): [string, string] => [code, 'degF']),
  ...CELSIUS_CODES.map((code): [string, string] => [code, 'Cel']),
  ['kg', 'kg'],
  ['g', 'g'],
  ['cm', 'cm'],
]);

interface ResolvedConversion {
  outcome: ConversionOutcome;
  sourceUnit: string | undefined;
}

function resolveConversion(
  quantity: Quantity | undefined,
  unitSystem: NormalizedUnitSystem | undefined,
): ResolvedConversion | undefined {
  const conversions = unitSystem ? CONVERSIONS_BY_UNIT_SYSTEM[unitSystem] : undefined;
  if (!(conversions && quantity) || quantity.value === undefined) {
    return undefined;
  }

  const systemIsAbsent = quantity.system === undefined || quantity.system === null;
  const codeIsAbsent = quantity.code === undefined || quantity.code === null;

  let sourceCode: string | undefined;
  if (quantity.system === SYSTEM_UCUM && !codeIsAbsent) {
    sourceCode = quantity.code;
  } else if (systemIsAbsent && codeIsAbsent) {
    sourceCode = quantity.unit;
  } else {
    return undefined;
  }

  const outcome = sourceCode ? conversions.get(sourceCode)?.(quantity.value) : undefined;
  return outcome && { outcome, sourceUnit: sourceCode && UNIT_BY_CODE.get(sourceCode) };
}

/** Converts a FHIR Quantity into `unitSystem` using its UCUM code, or `unit` when uncoded. */
export function convertQuantity(
  quantity: Quantity | undefined,
  unitSystem: NormalizedUnitSystem | undefined,
): ConversionResult | undefined {
  const resolved = resolveConversion(quantity, unitSystem);
  if (!resolved) {
    return undefined;
  }
  const { displayUnit: _displayUnit, ...result } = resolved.outcome;
  return result;
}

/**
 * Reports whether converting the quantity into `unitSystem` displays it in a different unit than
 * the source, which makes a reference range written in the source unit stale.
 */
export function convertsToDifferentUnit(
  quantity: Quantity | undefined,
  unitSystem: NormalizedUnitSystem | undefined,
): boolean {
  const resolved = resolveConversion(quantity, unitSystem);
  return !!resolved && resolved.outcome.displayUnit !== resolved.sourceUnit;
}
